import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const GIVING_URL = 'https://giving.myamplify.io//app/giving/clodo-reachingpeoplefarfromgod';

const GiveNow = () => {
  const [activeTab, setActiveTab] = useState('online');
  const [showGivingModal, setShowGivingModal] = useState(false);

  const openGivingModal = () => setShowGivingModal(true);
  const closeGivingModal = () => setShowGivingModal(false);

  // Lock body scroll when modal open & close on ESC
  useEffect(() => {
    if (showGivingModal) {
      document.body.style.overflow = 'hidden';
      const onKey = (e) => {
        if (e.key === 'Escape') closeGivingModal();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [showGivingModal]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-emerald-900">
      <Helmet>
        <title>Give Now | Support Kioo Radio 98.1FM</title>
        <meta name="description" content="Support Christian broadcasting among the Kissi People in Liberia, Sierra Leone, and Guinea. Your gift brings hope and the Gospel to communities across the Makona River Region." />
      </Helmet>

      {/* Hero */}
      <section className="relative py-10 md:py-14 overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>

        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-emerald-100 text-sm mb-5">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            Broadcasting Hope Amongst the Kissi People in Liberia, Sierra Leone, and Guinea
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
            Invest in{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-400">
              Eternal Impact
            </span>
          </h1>

          <p className="text-base md:text-lg text-emerald-100 mb-8 leading-relaxed max-w-3xl mx-auto">
            Your gift brings the Gospel of Jesus Christ to the{' '}
            <strong className="text-white">Kissi People of Liberia, Sierra Leone, and Guinea</strong>{' '}
            through Kioo Radio 98.1FM.
          </p>

          {/* Impact Stats */}
          <div className="grid grid-cols-3 gap-3 md:gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-white" data-testid="stat-nations">3</div>
              <div className="text-emerald-200 text-xs md:text-sm">Nations Reached</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-white" data-testid="stat-broadcasting">24/7</div>
              <div className="text-emerald-200 text-xs md:text-sm">Broadcasting</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-4xl font-bold text-white" data-testid="stat-listeners">3M+</div>
              <div className="text-emerald-200 text-xs md:text-sm">Estimated Listeners</div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column: Ways to Give + Questions About Giving */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">

            {/* Ways to Give */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col">
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  Ways to Give
                </h2>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                    { id: 'online', label: 'Online', icon: '💳' },
                    { id: 'check', label: 'By Check', icon: '✉️' },
                    { id: 'bank', label: 'Bank Transfer', icon: '🏦' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      data-testid={`tab-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2 rounded-full font-medium transition-all ${activeTab === tab.id
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="flex-1">
                  {activeTab === 'online' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Give securely online through our trusted giving platform. Your transaction is encrypted and secure.
                      </p>
                      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                        <p className="text-sm text-emerald-800">
                          <strong>Important:</strong> Please select{' '}
                          <strong>"Liberia Vox Radio"</strong> on the giving page as your designation to direct your gift to Kioo Radio.
                        </p>
                      </div>
                      <button
                        type="button"
                        data-testid="give-online-now-button"
                        onClick={openGivingModal}
                        className="w-full inline-flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-emerald-800 transition-all transform hover:scale-[1.01] shadow-lg"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                        Give Online Now
                      </button>
                      <div className="flex items-center gap-4 text-xs text-gray-500 justify-center pt-1">
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          SSL Secure
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Tax Deductible
                        </span>
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Receipt Emailed
                        </span>
                      </div>
                    </div>
                  )}

                  {activeTab === 'check' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Giving by check is a stewardship-friendly option that ensures 100% of your gift goes to the ministry.
                      </p>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="font-semibold text-gray-900 mb-2">Mail checks to:</p>
                        <address className="text-gray-600 not-italic">
                          <strong>Reaching People Far From God (RPFFG)</strong><br />
                          P.O. Box 113<br />
                          Banning, CA 92220, USA
                        </address>
                      </div>
                      <p className="text-sm text-gray-500">
                        Please write "Kioo Radio" in the memo line.
                      </p>
                    </div>
                  )}

                  {activeTab === 'bank' && (
                    <div className="space-y-4">
                      <p className="text-gray-600">
                        Support may also be given through a direct bank-to-bank transfer using ACH. This method is secure and reliable.
                      </p>
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                        <p className="text-sm text-blue-800">
                          For ACH transfer details, please contact us directly. We'll provide you with the necessary banking information.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Questions About Giving */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col">
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Questions About Giving?
                </h2>
                <p className="text-gray-600 mb-6">
                  We're here to help. Contact us for assistance with your gift or to learn more about supporting Kioo Radio.
                </p>

                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl p-6 border border-emerald-100 flex-1 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-md">
                      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Joseph Kebbie</h3>
                      <p className="text-emerald-700 text-sm">Donor Relations</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <a
                      href="mailto:admin@proudlyliberian.com"
                      data-testid="contact-email-link"
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-gray-700 hover:text-emerald-700 hover:shadow-md transition-all border border-emerald-100"
                    >
                      <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">admin@proudlyliberian.com</span>
                    </a>
                    <a
                      href="tel:+17197268610"
                      data-testid="contact-phone-link"
                      className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl text-gray-700 hover:text-emerald-700 hover:shadow-md transition-all border border-emerald-100"
                    >
                      <svg className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="font-medium">+1 719 726 8610</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Giving Modal (Popup with iframe) */}
      {showGivingModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-6 bg-black/70 backdrop-blur-sm"
          onClick={closeGivingModal}
          data-testid="giving-modal-overlay"
        >
          <div
            className="relative w-full max-w-5xl h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="giving-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gradient-to-r from-emerald-700 to-emerald-800 text-white flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div className="min-w-0">
                  <h3 className="font-bold text-base md:text-lg truncate">Give to Kioo Radio</h3>
                  <p className="text-xs text-emerald-100 truncate">Select "Liberia Vox Radio" on the giving page</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={GIVING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                  data-testid="giving-modal-open-new-tab"
                >
                  Open in new tab
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={closeGivingModal}
                  data-testid="giving-modal-close-button"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Iframe */}
            <div className="flex-1 relative bg-gray-50">
              <iframe
                src={GIVING_URL}
                title="Kioo Radio Online Giving"
                className="absolute inset-0 w-full h-full border-0"
                allow="payment *; clipboard-write"
                data-testid="giving-modal-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveNow;
